/**
 * RequestChangesPanel
 *
 * Slide-out right panel for submitting change requests
 * while viewing the site preview. Lets the customer add
 * multiple items (category + location + description),
 * submit them as a batch, and see recent revision history.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  X,
  Plus,
  Send,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import {
  getRevisions,
  submitRevisionBatch,
  type Revision,
  type RevisionItem,
  type RevisionCategory,
  type RevisionLocation,
  categoryLabels,
  locationLabels,
} from '../api/website';

// =============================================================================
// TYPES
// =============================================================================

interface RequestChangesPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called when the panel should close */
  onClose: () => void;
}

interface FormItem {
  id: string;
  category: RevisionCategory;
  location: RevisionLocation;
  description: string;
}

// =============================================================================
// HELPERS
// =============================================================================

const createEmptyItem = (): FormItem => ({
  id: `item_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
  category: 'text',
  location: 'general',
  description: '',
});

// =============================================================================
// COMPONENT
// =============================================================================

export function RequestChangesPanel({ isOpen, onClose }: RequestChangesPanelProps) {
  const [items, setItems] = useState<FormItem[]>([createEmptyItem()]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [recentRevisions, setRecentRevisions] = useState<Revision[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Load recent revisions when panel opens
  useEffect(() => {
    if (!isOpen) return;
    setLoadingHistory(true);
    getRevisions()
      .then(({ revisions }) => {
        // Show only the last 5
        setRecentRevisions(revisions.slice(0, 5));
      })
      .catch(() => {
        // Silently fail — history is nice-to-have
      })
      .finally(() => setLoadingHistory(false));
  }, [isOpen]);

  // Clear success message after 4 seconds
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(''), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const addItem = useCallback(() => {
    if (items.length >= 10) return;
    setItems((prev) => [...prev, createEmptyItem()]);
  }, [items.length]);

  const removeItem = useCallback((id: string) => {
    setItems((prev) => (prev.length <= 1 ? prev : prev.filter((item) => item.id !== id)));
  }, []);

  const updateItem = useCallback((id: string, field: keyof FormItem, value: string) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  }, []);

  const handleSubmit = useCallback(async () => {
    setError('');
    setSuccess('');

    const validItems = items.filter((item) => item.description.trim());
    if (validItems.length === 0) {
      setError('Please describe at least one change.');
      return;
    }

    setIsSubmitting(true);

    try {
      const revisionItems: RevisionItem[] = validItems.map((item) => ({
        category: item.category,
        location: item.location,
        description: item.description.trim(),
      }));

      await submitRevisionBatch(revisionItems);

      setSuccess(
        `${validItems.length} change request${validItems.length > 1 ? 's' : ''} submitted!`
      );
      setItems([createEmptyItem()]);

      // Refresh history
      getRevisions()
        .then(({ revisions }) => setRecentRevisions(revisions.slice(0, 5)))
        .catch(() => {});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  }, [items]);

  const filledCount = items.filter((i) => i.description.trim()).length;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-3.5 h-3.5 text-green-600" />;
      case 'processing':
        return <Loader2 className="w-3.5 h-3.5 text-blue-600 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-3.5 h-3.5 text-red-600" />;
      default:
        return <Clock className="w-3.5 h-3.5 text-yellow-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Done';
      case 'processing':
        return 'In Progress';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-40 flex flex-col transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Request Changes</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Describe what you'd like updated
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Success message */}
          {success && (
            <div className="mx-5 mt-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 shrink-0" />
              {success}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="mx-5 mt-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Change items */}
          <div className="px-5 py-4 space-y-4">
            {items.map((item, index) => (
              <div
                key={item.id}
                className="border border-gray-200 rounded-lg p-3 relative"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
                    Change #{index + 1}
                  </span>
                  {items.length > 1 && (
                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-0.5 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2 mb-3">
                  <select
                    value={item.category}
                    onChange={(e) =>
                      updateItem(item.id, 'category', e.target.value)
                    }
                    className="text-sm px-2.5 py-1.5 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {(Object.keys(categoryLabels) as RevisionCategory[]).map(
                      (key) => (
                        <option key={key} value={key}>
                          {categoryLabels[key]}
                        </option>
                      )
                    )}
                  </select>

                  <select
                    value={item.location}
                    onChange={(e) =>
                      updateItem(item.id, 'location', e.target.value)
                    }
                    className="text-sm px-2.5 py-1.5 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    {(Object.keys(locationLabels) as RevisionLocation[]).map(
                      (key) => (
                        <option key={key} value={key}>
                          {locationLabels[key]}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <textarea
                  value={item.description}
                  onChange={(e) =>
                    updateItem(item.id, 'description', e.target.value)
                  }
                  rows={2}
                  placeholder="Describe the change you'd like..."
                  className="w-full text-sm px-3 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
              </div>
            ))}

            {/* Add another */}
            {items.length < 10 && (
              <button
                onClick={addItem}
                className="w-full py-2.5 border-2 border-dashed border-gray-200 rounded-lg text-gray-500 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Another Change
              </button>
            )}
          </div>

          {/* Recent History */}
          {recentRevisions.length > 0 && (
            <div className="px-5 pb-4 border-t border-gray-100 pt-4">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">
                Recent Requests
              </h3>
              <div className="space-y-2">
                {recentRevisions.map((revision) => (
                  <div
                    key={revision.id}
                    className="flex items-start gap-2 text-sm"
                  >
                    {getStatusIcon(revision.status)}
                    <div className="min-w-0 flex-1">
                      <p className="text-gray-700 truncate">{revision.description}</p>
                      <p className="text-xs text-gray-400">
                        {getStatusLabel(revision.status)} · {new Date(revision.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <a
                href="/revisions"
                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2"
              >
                View all history
                <ChevronRight className="w-3 h-3" />
              </a>
            </div>
          )}

          {loadingHistory && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
            </div>
          )}
        </div>

        {/* Footer — submit button */}
        <div className="px-5 py-4 border-t border-gray-200 shrink-0 bg-gray-50">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || filledCount === 0}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Submit {filledCount} Request{filledCount !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
}
